import { useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { useSsupSession } from "./auth/useSsupSession";
import { useSsupAuthActions } from "./auth/useSsupAuthActions";
import { SignInPanel } from "./auth/SignInPanel";
import { MfaChallenge } from "./auth/MfaChallenge";
import { AuthLayout } from "./shell/AuthLayout";
import { PortalShell } from "./shell/PortalShell";
import { PortalDocs } from "./docs/PortalDocs";

/**
 * Top-level router for the SSUP partner validation portal. Owns nothing but the
 * "which screen" decision and the docs toggle; identity lives in
 * {@link useSsupSession}, sign-in actions in {@link useSsupAuthActions}, and each
 * screen is its own component.
 */
export function App() {
  const session = useSsupSession();
  const actions = useSsupAuthActions();
  const [showDocs, setShowDocs] = useState(false);

  // The partner guide is readable at any time, signed in or out.
  if (showDocs) {
    return <PortalDocs onClose={() => setShowDocs(false)} />;
  }

  if (!session.ready) {
    return (
      <AuthLayout>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, py: 4 }}>
          <CircularProgress size={22} />
          <Typography color="text.secondary">Connecting to storyworm-ssup…</Typography>
        </Box>
      </AuthLayout>
    );
  }

  // A sign-in paused on MFA: complete the TOTP challenge before we have a user.
  if (actions.resolver && !session.user) {
    return (
      <AuthLayout>
        <MfaChallenge resolver={actions.resolver} onCancel={actions.cancelMfa} />
      </AuthLayout>
    );
  }

  if (session.user) {
    return (
      <PortalShell
        session={session}
        onOpenDocs={() => setShowDocs(true)}
        onSignOut={actions.signOutUser}
      />
    );
  }

  return (
    <AuthLayout>
      <SignInPanel actions={actions} onOpenDocs={() => setShowDocs(true)} />
    </AuthLayout>
  );
}
